import {
  ASSET_KINDS,
  MAX_INSTRUMENT_NAME_LENGTH,
  MAX_MEMO_LENGTH,
  MAX_PURCHASE_LOTS_PER_HOLDING,
  MAX_TICKER_LENGTH,
  MAX_VENUE_LENGTH,
} from "./constants";
import {
  addDecimalStrings,
  compareDecimalStrings,
  isNonNegativeDecimalString,
  isPositiveDecimalString,
  normalizeDecimalString,
  type DecimalString,
} from "./decimal";
import { isIso4217CurrencyCode } from "./currency";
import {
  normalizeInstrumentName,
  normalizeInstrumentTicker,
  normalizeVenue,
} from "./identity";
import {
  isIsoCalendarDate,
  type AssetKindV1,
  type HoldingV1,
  type LocalPortfolioRecordV1,
  type PurchaseLotV1,
} from "./schema";

export const HOLDING_EDITOR_DRAFT_VERSION = 1 as const;

const TICKER_PATTERN = /^[A-Z0-9][A-Z0-9.-]*$/;
const VENUE_PATTERN = /^[A-Z0-9][A-Z0-9._:-]*$/;

export type HoldingCostMode = "average" | "lots";
export type HoldingQuoteMode = "preserve" | "manual" | "clear";

export interface PurchaseLotEditorDraftV1 {
  readonly draftLotId: string;
  readonly sourceLotId?: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly purchasedAt: string;
}

export interface HoldingEditorDraftV1 {
  readonly draftVersion: typeof HOLDING_EDITOR_DRAFT_VERSION;
  readonly assetKind: AssetKindV1;
  readonly ticker: string;
  readonly name: string;
  readonly market: string;
  readonly exchange: string;
  readonly currency: string;
  readonly quantity: string;
  readonly costMode: HoldingCostMode;
  /** Currency of averageBuyPrice or purchase lot unitPrice values. */
  readonly costCurrency?: string;
  /** Inactive cost data is retained until a successful canonical commit. */
  readonly averageBuyPrice: string;
  readonly purchaseLots: readonly PurchaseLotEditorDraftV1[];
  readonly quoteMode: HoldingQuoteMode;
  readonly currentPrice: string;
  readonly firstPurchasedAt: string;
  readonly memo: string;
}

export type HoldingEditorField =
  | "identity"
  | "ticker"
  | "name"
  | "market"
  | "exchange"
  | "currency"
  | "costCurrency"
  | "quantity"
  | "averageBuyPrice"
  | "purchaseLots"
  | `purchaseLots.${string}.quantity`
  | `purchaseLots.${string}.unitPrice`
  | `purchaseLots.${string}.purchasedAt`
  | "currentPrice"
  | "firstPurchasedAt"
  | "memo";

export interface HoldingEditorIssue {
  readonly code:
    | "required"
    | "invalid_format"
    | "invalid_value"
    | "future_date"
    | "duplicate"
    | "resource_limit"
    | "lot_quantity_mismatch";
  readonly field: HoldingEditorField;
  readonly message: string;
}

export interface HoldingEditorValidation {
  readonly valid: boolean;
  readonly issues: readonly HoldingEditorIssue[];
}

export interface CostModeSwitchPlan {
  readonly from: HoldingCostMode;
  readonly to: HoldingCostMode;
  readonly requiresConfirmation: boolean;
  readonly message: string;
}

export interface CreateHoldingEditorDraftOptions {
  readonly holdingId?: string;
  readonly createDraftLotId: () => string;
}

function emptyLot(
  createDraftLotId: () => string,
): PurchaseLotEditorDraftV1 {
  return {
    draftLotId: createDraftLotId(),
    quantity: "",
    unitPrice: "",
    purchasedAt: "",
  };
}

function draftLot(
  lot: PurchaseLotV1,
  createDraftLotId: () => string,
): PurchaseLotEditorDraftV1 {
  return {
    draftLotId: createDraftLotId(),
    sourceLotId: lot.lotId,
    quantity: lot.quantity,
    unitPrice: lot.unitPrice,
    purchasedAt: lot.purchasedAt ?? "",
  };
}

export function createHoldingEditorDraft(
  record: LocalPortfolioRecordV1 | null,
  options: CreateHoldingEditorDraftOptions,
): HoldingEditorDraftV1 {
  const holding = options.holdingId
    ? record?.document.holdings.find(
        ({ holdingId }) => holdingId === options.holdingId,
      )
    : undefined;
  const quote = holding
    ? record?.manualQuotes?.quotes.find(
        ({ holdingId }) => holdingId === holding.holdingId,
      )
    : undefined;
  const lots =
    holding?.costBasis?.mode === "lots"
      ? holding.costBasis.purchaseLots.map((lot) =>
          draftLot(lot, options.createDraftLotId),
        )
      : [emptyLot(options.createDraftLotId)];

  return {
    draftVersion: HOLDING_EDITOR_DRAFT_VERSION,
    assetKind: holding?.assetKind ?? "stock",
    ticker: holding?.ticker ?? "",
    name: holding?.name ?? "",
    market: holding?.market ?? "",
    exchange: holding?.exchange ?? "",
    currency: holding?.currency ?? record?.document.currency ?? "KRW",
    quantity: holding?.quantity ?? "",
    costMode: holding?.costBasis?.mode ?? "average",
    costCurrency:
      holding?.costBasis?.currency ??
      holding?.currency ??
      record?.document.currency ??
      "KRW",
    averageBuyPrice:
      holding?.costBasis?.mode === "average"
        ? holding.costBasis.averageBuyPrice
        : "",
    purchaseLots: lots,
    quoteMode: holding ? "preserve" : "clear",
    currentPrice: quote?.currentPrice ?? "",
    firstPurchasedAt: holding?.firstPurchasedAt ?? "",
    memo: holding?.memo ?? "",
  };
}

export function planCostModeSwitch(
  draft: HoldingEditorDraftV1,
  to: HoldingCostMode,
): CostModeSwitchPlan {
  const from = draft.costMode;
  if (from === to) {
    return {
      from,
      to,
      requiresConfirmation: false,
      message: "",
    };
  }

  if (from === "average") {
    const hasValue = draft.averageBuyPrice.trim().length > 0;
    return {
      from,
      to,
      requiresConfirmation: hasValue,
      message: hasValue
        ? `저장하면 평균단가 ${draft.averageBuyPrice}는 장부의 별도 필드로 남지 않아요. 입력값은 초안에 보관되므로 전환을 취소하거나 다시 돌아올 수 있어요.`
        : "매수 내역의 수량 합계는 보유 수량과 정확히 같아야 해요.",
    };
  }

  const populatedLots = draft.purchaseLots.filter(
    (lot) =>
      lot.quantity.trim() ||
      lot.unitPrice.trim() ||
      lot.purchasedAt.trim(),
  );
  return {
    from,
    to,
    requiresConfirmation: populatedLots.length > 0,
    message:
      populatedLots.length > 0
        ? `저장하면 매수 내역 ${populatedLots.length}건의 날짜·수량·단가가 장부에서 사라져요. 입력값은 초안에 보관되므로 다시 전환하면 복구할 수 있어요.`
        : "평균단가를 입력하지 않으면 원가 미입력 상태로 저장돼요.",
  };
}

function trimmed(value: string): string {
  return value.trim();
}

function duplicateIdentityIssue(
  draft: HoldingEditorDraftV1,
  record: LocalPortfolioRecordV1 | null,
  holdingId?: string,
): HoldingEditorIssue | null {
  if (!record) {
    return null;
  }

  const ticker = trimmed(draft.ticker)
    ? normalizeInstrumentTicker(draft.ticker)
    : undefined;
  const name = trimmed(draft.name)
    ? normalizeInstrumentName(draft.name)
    : undefined;
  const venue = trimmed(draft.exchange)
    ? `exchange:${normalizeVenue(draft.exchange)}`
    : trimmed(draft.market)
      ? `market:${normalizeVenue(draft.market)}`
      : undefined;

  for (const other of record.document.holdings) {
    if (other.holdingId === holdingId) {
      continue;
    }
    if (ticker && other.ticker) {
      const otherTicker = normalizeInstrumentTicker(other.ticker);
      if (ticker !== otherTicker) {
        continue;
      }
      const otherVenue = other.exchange
        ? `exchange:${normalizeVenue(other.exchange)}`
        : other.market
          ? `market:${normalizeVenue(other.market)}`
          : undefined;
      if (!venue || !otherVenue) {
        return {
          code: "duplicate",
          field: venue ? "ticker" : "exchange",
          message: `이미 등록된 ${ticker}과 구분하려면 두 종목 모두 거래소를 입력해 주세요.`,
        };
      }
      if (venue === otherVenue) {
        return {
          code: "duplicate",
          field: "ticker",
          message: `${venue.split(":")[1]} · ${ticker}가 이미 장부에 있어요.`,
        };
      }
    } else if (!ticker && name && !other.ticker && other.name) {
      if (name === normalizeInstrumentName(other.name)) {
        return {
          code: "duplicate",
          field: "name",
          message: "같은 이름의 임시 종목이 이미 있어 구분할 수 없어요.",
        };
      }
    }
  }
  return null;
}

function pushTextIssues(
  issues: HoldingEditorIssue[],
  field: "ticker" | "name" | "market" | "exchange" | "memo",
  value: string,
  maximumLength: number,
): void {
  if (value.length > maximumLength) {
    issues.push({
      code: "resource_limit",
      field,
      message: `${maximumLength}자 이하로 입력해 주세요.`,
    });
  }
}

export function validateHoldingEditorDraft(
  draft: HoldingEditorDraftV1,
  context: {
    readonly record: LocalPortfolioRecordV1 | null;
    readonly holdingId?: string;
    readonly today: string;
  },
): HoldingEditorValidation {
  const issues: HoldingEditorIssue[] = [];
  const ticker = trimmed(draft.ticker).toUpperCase();
  const name = trimmed(draft.name);
  const market = trimmed(draft.market).toUpperCase();
  const exchange = trimmed(draft.exchange).toUpperCase();
  const currency = trimmed(draft.currency).toUpperCase();
  const costCurrency = trimmed(
    draft.costCurrency ?? draft.currency,
  ).toUpperCase();

  if (!ticker && !name) {
    issues.push({
      code: "required",
      field: "identity",
      message: "티커 또는 종목명 중 하나 이상을 입력해 주세요.",
    });
  }
  if (ticker && !TICKER_PATTERN.test(ticker)) {
    issues.push({
      code: "invalid_format",
      field: "ticker",
      message: "티커는 영문 대문자, 숫자, 점, 하이픈만 사용할 수 있어요.",
    });
  }
  if (market && !VENUE_PATTERN.test(market)) {
    issues.push({
      code: "invalid_format",
      field: "market",
      message: "시장은 영문 대문자, 숫자, 점, 밑줄, 콜론, 하이픈으로 입력해 주세요.",
    });
  }
  if (exchange && !VENUE_PATTERN.test(exchange)) {
    issues.push({
      code: "invalid_format",
      field: "exchange",
      message: "거래소는 영문 대문자, 숫자, 점, 밑줄, 콜론, 하이픈으로 입력해 주세요.",
    });
  }
  if (!isIso4217CurrencyCode(currency)) {
    issues.push({
      code: "invalid_format",
      field: "currency",
      message: "지원하는 ISO 4217 통화 코드로 입력해 주세요.",
    });
  }
  if (!isIso4217CurrencyCode(costCurrency)) {
    issues.push({
      code: "invalid_format",
      field: "costCurrency",
      message: "원가 통화는 지원하는 ISO 4217 코드로 입력해 주세요.",
    });
  }
  if (!isPositiveDecimalString(trimmed(draft.quantity))) {
    issues.push({
      code: "invalid_value",
      field: "quantity",
      message: "보유 수량은 0보다 큰 소수로 입력해 주세요.",
    });
  }

  if (
    draft.costMode === "average" &&
    trimmed(draft.averageBuyPrice) &&
    !isNonNegativeDecimalString(trimmed(draft.averageBuyPrice))
  ) {
    issues.push({
      code: "invalid_value",
      field: "averageBuyPrice",
      message: "평균단가는 0 이상의 소수로 입력해 주세요.",
    });
  }

  if (draft.costMode === "lots") {
    if (
      draft.purchaseLots.length === 0 ||
      draft.purchaseLots.length > MAX_PURCHASE_LOTS_PER_HOLDING
    ) {
      issues.push({
        code: "resource_limit",
        field: "purchaseLots",
        message: `매수 내역은 1개 이상 ${MAX_PURCHASE_LOTS_PER_HOLDING}개 이하로 입력해 주세요.`,
      });
    }
    const validLotQuantities: DecimalString[] = [];
    for (const lot of draft.purchaseLots) {
      const quantityField =
        `purchaseLots.${lot.draftLotId}.quantity` as const;
      const unitPriceField =
        `purchaseLots.${lot.draftLotId}.unitPrice` as const;
      const dateField =
        `purchaseLots.${lot.draftLotId}.purchasedAt` as const;
      const quantity = trimmed(lot.quantity);
      const unitPrice = trimmed(lot.unitPrice);
      const purchasedAt = trimmed(lot.purchasedAt);

      if (!isPositiveDecimalString(quantity)) {
        issues.push({
          code: "invalid_value",
          field: quantityField,
          message: "매수 수량은 0보다 커야 해요.",
        });
      } else {
        validLotQuantities.push(normalizeDecimalString(quantity));
      }
      if (!isNonNegativeDecimalString(unitPrice)) {
        issues.push({
          code: "invalid_value",
          field: unitPriceField,
          message: "매수 단가는 0 이상의 소수로 입력해 주세요.",
        });
      }
      if (purchasedAt && !isIsoCalendarDate(purchasedAt)) {
        issues.push({
          code: "invalid_format",
          field: dateField,
          message: "실제로 존재하는 날짜를 입력해 주세요.",
        });
      } else if (purchasedAt && purchasedAt > context.today) {
        issues.push({
          code: "future_date",
          field: dateField,
          message: "미래의 매수일은 저장할 수 없어요.",
        });
      }
    }
    if (
      validLotQuantities.length === draft.purchaseLots.length &&
      isPositiveDecimalString(trimmed(draft.quantity))
    ) {
      try {
        const normalizedQuantity = normalizeDecimalString(
          trimmed(draft.quantity) as DecimalString,
        );
        const total = addDecimalStrings(validLotQuantities);
        if (
          compareDecimalStrings(
            total,
            normalizedQuantity,
          ) !== 0
        ) {
          issues.push({
            code: "lot_quantity_mismatch",
            field: "purchaseLots",
            message: `매수 내역 수량 합계 ${total}주가 보유 수량 ${normalizedQuantity}주와 정확히 같아야 해요.`,
          });
        }
      } catch {
        issues.push({
          code: "invalid_value",
          field: "purchaseLots",
          message: "매수 내역 수량 합계를 안전하게 계산할 수 없어요.",
        });
      }
    }
  }

  if (
    draft.quoteMode === "manual" &&
    !isNonNegativeDecimalString(trimmed(draft.currentPrice))
  ) {
    issues.push({
      code: "invalid_value",
      field: "currentPrice",
      message: "현재가는 0 이상의 소수로 입력해 주세요.",
    });
  }
  const firstPurchasedAt = trimmed(draft.firstPurchasedAt);
  if (firstPurchasedAt && !isIsoCalendarDate(firstPurchasedAt)) {
    issues.push({
      code: "invalid_format",
      field: "firstPurchasedAt",
      message: "실제로 존재하는 날짜를 입력해 주세요.",
    });
  } else if (firstPurchasedAt && firstPurchasedAt > context.today) {
    issues.push({
      code: "future_date",
      field: "firstPurchasedAt",
      message: "미래의 최초 매수일은 저장할 수 없어요.",
    });
  }

  pushTextIssues(issues, "ticker", draft.ticker, MAX_TICKER_LENGTH);
  pushTextIssues(issues, "name", draft.name, MAX_INSTRUMENT_NAME_LENGTH);
  pushTextIssues(issues, "market", draft.market, MAX_VENUE_LENGTH);
  pushTextIssues(issues, "exchange", draft.exchange, MAX_VENUE_LENGTH);
  pushTextIssues(issues, "memo", draft.memo, MAX_MEMO_LENGTH);
  if (!ASSET_KINDS.includes(draft.assetKind)) {
    issues.push({
      code: "invalid_value",
      field: "identity",
      message: "지원하는 자산 종류를 선택해 주세요.",
    });
  }

  const duplicate = duplicateIdentityIssue(
    draft,
    context.record,
    context.holdingId,
  );
  if (duplicate) {
    issues.push(duplicate);
  }

  return { valid: issues.length === 0, issues };
}

export function issueForHoldingEditorField(
  issues: readonly HoldingEditorIssue[],
  field: HoldingEditorField,
): HoldingEditorIssue | undefined {
  return issues.find((issue) => issue.field === field);
}

export function sourceHoldingForDraft(
  record: LocalPortfolioRecordV1 | null,
  holdingId?: string,
): HoldingV1 | null {
  return (
    record?.document.holdings.find(
      (holding) => holding.holdingId === holdingId,
    ) ?? null
  );
}
